// All simulation math is integer-only so that the player's device, the server
// and every spectator compute bit-identical results from the same inputs.
// Unit: 1 "sub" = 1/16 pixel.

export const TICK_HZ = 60;
export const SUB = 16;
export const TILE_PX = 16;
export const TILE = TILE_PX * SUB;
export const ROWS = 15;
export const VIEW_W_PX = 400;
export const VIEW_H_PX = ROWS * TILE_PX;
export const VIEW_W = VIEW_W_PX * SUB;
export const WORLD_H = ROWS * TILE;

export const PLAYER_W = 10 * SUB;
export const PLAYER_H = 14 * SUB;
export const START_X = 3 * TILE;
export const START_GROUND = 3;

// Movement (sub/tick, sub/tick²). Jump apex ≈ 3.5 tiles, max horizontal reach ≈ 4.9 tiles.
export const RUN_MAX = 36;
export const ACCEL_GROUND = 3;
export const ACCEL_AIR = 2;
export const FRICTION_GROUND = 4;
export const FRICTION_AIR = 1;
export const GRAVITY = 6;
export const JUMP_V = 104;
export const JUMP_CUT = 40;
export const FALL_MAX = 128;
export const COYOTE_TICKS = 6;
export const JUMP_BUFFER_TICKS = 6;

// The camera scrolls on its own; fall off the left edge and you're out.
export const SCROLL_DELAY_TICKS = 90;
export const SCROLL_MIN = 10;
export const SCROLL_MAX = 32;
export const SCROLL_RAMP_TICKS = 7200;
export const CAMERA_LEAD = Math.floor((VIEW_W * 55) / 100);

export const BTN_LEFT = 1;
export const BTN_RIGHT = 2;
export const BTN_JUMP = 4;

// Spectator sabotage.
export const SAB_KINDS = ['speed', 'rain', 'dark', 'flip'] as const;
export type SabKind = (typeof SAB_KINDS)[number];
export const SAB_DURATION: Record<SabKind, number> = { speed: 240, rain: 240, dark: 360, flip: 240 };
export const SAB_LABEL: Record<SabKind, string> = { speed: 'SPEED', rain: 'RAIN', dark: 'DARK', flip: 'FLIP' };
export const SPEED_BOOST = 14;
export const SPEED_BOOST_CAP = 42;

// Scoring.
export const COIN_POINTS = 10;
export const SURVIVE_BONUS = 50;
/** Going further than one of the last few players (their line is drawn on the course). */
export const RIVAL_BONUS = 100;
export const RIVAL_COUNT = 3;
/** Shorter runs don't become rivals: a lost turn shouldn't hand out free points. */
export const RIVAL_MIN_DIST = 20;

/** The big word on the screen when a run ends (and on its replay's link preview). */
export const DEATH_TITLE: Record<string, string> = {
  fell: 'SPLAT!',
  spiked: 'SKEWERED!',
  'left behind': 'SQUISHED!',
  'rained on': 'BONK!',
  timeout: 'LAGGED OUT',
  left: 'RAGE QUIT',
};

/** "in front of 23 people": who saw it, not counting the player (`watchers` includes them). */
export function audience(watchers: number): string {
  const n = watchers - 1;
  return n <= 0 ? 'with nobody watching' : `in front of ${n.toLocaleString('en')} ${n === 1 ? 'person' : 'people'}`;
}

/** "<player> got spiked" — used in chat and result screens. */
export const DEATH_TEXT: Record<string, string> = {
  fell: 'fell into the void',
  spiked: 'got spiked',
  'left behind': 'got left behind',
  'rained on': 'got rained on',
  timeout: 'lagged out',
  left: 'rage quit',
  cheat: 'was too fast to be true',
  desync: 'desynced',
};
